import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, getAdminApiUrl } from '../lib/supabase';
import { validatePassword } from '../lib/passwordValidation';
import MFAManagement from './MFAManagement';
import Logo from './Logo';

interface User {
  id: string;
  email: string;
  account_email_id?: string;
  created_at: string;
  is_admin?: boolean;
  admin_role?: string;
  is_confirmed?: boolean;
  last_sign_in?: string;
  user_metadata?: any;
  profile?: {
    first_name?: string;
    last_name?: string;
    business_name?: string;
    profile_type?: string;
  };
}

/*
interface AuthUser { // Unused
  id: string;
  email: string;
  created_at: string;
  email_confirmed_at?: string;
  last_sign_in_at?: string;
  user_metadata?: any;
  profile?: {
    first_name?: string;
    last_name?: string;
    business_name?: string;
    profile_type?: string;
  };
}
*/

const Dashboard: React.FC = () => {
  const { user, isAdmin, adminRole, hasMFA, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [users, setUsers] = useState<User[]>([]);
  // const [authUsers] = useState<AuthUser[]>([]); // Unused
  const [loading, setLoading] = useState(false);
  // Separate search states for each page
  const [homeSearchTerm, setHomeSearchTerm] = useState('');
  const [userManagementSearchTerm, setUserManagementSearchTerm] = useState('');
  
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [activeSection, setActiveSection] = useState<'home' | 'users' | 'serviceFees'>('home');
  const [totalAuthUsersCount, setTotalAuthUsersCount] = useState<number>(0);
  const [pendingConfirmationsCount, setPendingConfirmationsCount] = useState<number>(0);
  const [selectedTile, setSelectedTile] = useState<'all' | 'pending' | 'admin' | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [homePage, setHomePage] = useState(1);
  const itemsPerPage = 50;
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [createAdminLoading, setCreateAdminLoading] = useState(false);
  const [createAdminError, setCreateAdminError] = useState<string | null>(null);
  const [createAdminSuccess, setCreateAdminSuccess] = useState<string | null>(null);
  
  // Create Admin Form State
  const [createAdminForm, setCreateAdminForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    businessName: '',
    abnNumber: '',
    profileType: 'individual',
    adminRole: 'admin'
  });

  useEffect(() => {
    if (!isAdmin) return;
    
    // Only fetch users if we don't have restored state
    if (!location.state?.preserveState) {
      fetchUsers();
    }
    fetchTotalAuthUsersCount();
    fetchPendingConfirmationsCount();
  }, [isAdmin, location.state?.preserveState]);

  // Prevent non-super_admin users from accessing User Management section
  useEffect(() => {
    if (activeSection === 'users' && adminRole !== 'super_admin') {
      setActiveSection('home');
    }
  }, [activeSection, adminRole]);

  // Redirect non-super_admin users if they try to access User Management
  useEffect(() => {
    if (adminRole && adminRole !== 'super_admin' && activeSection === 'users') {
      setActiveSection('home');
    }
  }, [adminRole, activeSection]);

  useEffect(() => {
    // Don't run this effect if we're in the middle of restoring state
    if (location.state?.preserveState) return;
    
    if (activeSection === 'home') {
      // Home page: use home search term
      if (homeSearchTerm.trim() === '') {
        setFilteredUsers([]); // Home page shows search results only
      }
      // Reset to page 1 when search/filter changes
      setHomePage(1);
    } else {
      // User Management page: use user management search term
      if (userManagementSearchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const searchLower = userManagementSearchTerm.toLowerCase();
      const filtered = users.filter(user => {
        const email = user.email || user.account_email_id;
        return (email && email.toLowerCase().includes(searchLower)) ||
               (user.id && user.id.toLowerCase().includes(searchLower));
      });
      setFilteredUsers(filtered);
    }
    }
  }, [homeSearchTerm, userManagementSearchTerm, users, activeSection, location.state?.preserveState]);

  // Restore dashboard state when returning from other pages
  useEffect(() => {
    if (location.state?.preserveState) {
      const { activeSection: savedSection, homeSearchTerm: savedHomeSearch, filteredUsers: savedFilteredUsers, userManagementSearchTerm: savedUserSearch, currentPage: savedPage } = location.state;
      

      
      if (savedSection) {
        setActiveSection(savedSection);
      }
      if (savedHomeSearch !== undefined) {
        setHomeSearchTerm(savedHomeSearch);
      }
      if (savedUserSearch !== undefined) {
        setUserManagementSearchTerm(savedUserSearch);
      }
      if (savedPage) {
        setCurrentPage(savedPage);
      }
      if (savedFilteredUsers) {
        setFilteredUsers(savedFilteredUsers);
      }
      
      // Clear the state to prevent re-restoration
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Fetch users after state restoration if needed
  useEffect(() => {
    if (isAdmin && users.length === 0 && !location.state?.preserveState) {
      fetchUsers();
    }
  }, [isAdmin, users.length, location.state?.preserveState]);

  // Handle search term restoration and recalculate filteredUsers if needed
  useEffect(() => {
    // Only run this effect after state restoration is complete
    if (location.state?.preserveState) return;
    

    
    if (activeSection === 'home' && homeSearchTerm.trim()) {
      // If we're on home page and have a search term, we need to perform the search
      // This handles the case where state was restored but search results need to be fetched
      if (filteredUsers.length === 0) {

        searchUsersInAuth(homeSearchTerm.trim());
      }
    } else if (activeSection === 'users' && userManagementSearchTerm.trim()) {
      // If we're on users page and have a search term, filter the users
      if (filteredUsers.length === 0 || filteredUsers.length === users.length) {

        searchUsersInProfiles(userManagementSearchTerm.trim());
      }
    }
  }, [activeSection, homeSearchTerm, userManagementSearchTerm, filteredUsers.length, users.length, location.state?.preserveState]);

  // Handle tile-based filtering when filteredUsers change
  useEffect(() => {
    if (selectedTile && filteredUsers.length > 0) {
      if (selectedTile === 'pending') {
        // Filter to show only pending confirmation users
        const pendingUsers = filteredUsers.filter((user: any) => 
          !(user as any).email_confirmed_at || (user as any).email_confirmed_at === '-'
        );
        setFilteredUsers(pendingUsers);
      } else if (selectedTile === 'admin') {
        // Filter to show only admin users
        const adminUsers = filteredUsers.filter((user: any) => (user as any).is_admin);
        setFilteredUsers(adminUsers);
      }
    }
  }, [selectedTile, filteredUsers]);

  // Handle tile-based filtering when users change (for admin tile)
  useEffect(() => {
    if (selectedTile === 'admin' && users.length > 0) {
      setFilteredUsers(users);
    }
  }, [selectedTile, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // First, get admin users from user_profiles table
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, account_email_id, created_at, is_admin, admin_role, first_name, last_name, business_name, profile_type')
        .eq('is_admin', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      if (!data || data.length === 0) {
        setUsers([]);
        return;
      }

      // Get session for Auth API call
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        // Fallback: use profile data without confirmation status
        const validUsers = data.filter(user => 
          user && 
          typeof user === 'object' && 
          user.id && 
          (user.email || user.account_email_id) // Allow email OR account_email_id
        ).map(user => {
          // Use account_email_id as fallback if email is null
          const effectiveEmail = user.email || user.account_email_id || null;
          return {
            ...user,
            email: effectiveEmail, // Ensure email field is populated
            profile: {
              first_name: user.first_name,
              last_name: user.last_name,
              business_name: user.business_name,
              profile_type: user.profile_type
            }
          };
        });
        setUsers(validUsers);
        return;
      }

      // Fetch confirmation status from Auth API for admin users
      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'listUsers',
          page: 1,
          limit: 1000, // Get enough users to find all admin users
          search: '',
          filter: {}
        })
      });

      if (response.ok) {
        const { users: authUsers } = await response.json();
        
        // Create a map of auth users by ID for quick lookup
        const authUsersMap = new Map();
        if (authUsers && authUsers.length > 0) {
          authUsers.forEach((user: any) => {
            authUsersMap.set(user.id, user);
          });
        }

        // Combine profile data with auth data (including email_confirmed_at)
        const validUsers = data.filter(user => 
          user && 
          typeof user === 'object' && 
          user.id && 
          (user.email || user.account_email_id) // Allow email OR account_email_id
        ).map(user => {
          const authUser = authUsersMap.get(user.id);
          // Prefer Auth email, then profile email, then account_email_id
          const effectiveEmail = authUser?.email || user.email || user.account_email_id || null;
          return {
            ...user,
            email: effectiveEmail, // Ensure email field is populated with effective email
            // Include confirmation status from Auth API
            email_confirmed_at: authUser?.email_confirmed_at,
            last_sign_in_at: authUser?.last_sign_in_at,
            user_metadata: authUser?.user_metadata,
            profile: {
              first_name: user.first_name,
              last_name: user.last_name,
              business_name: user.business_name,
              profile_type: user.profile_type
            }
          };
        });
        
        setUsers(validUsers);
        
        // Only set filteredUsers if we're not in the middle of restoring state
        // and if we're on the User Management section or if filteredUsers is empty
        if (!location.state?.preserveState && (activeSection === 'users' || filteredUsers.length === 0)) {
          setFilteredUsers(validUsers);
        }
      } else {
        // Fallback: use profile data without confirmation status
        const validUsers = data.filter(user => 
          user && 
          typeof user === 'object' && 
          user.id && 
          (user.email || user.account_email_id) // Allow email OR account_email_id
        ).map(user => {
          // Use account_email_id as fallback if email is null
          const effectiveEmail = user.email || user.account_email_id || null;
          return {
            ...user,
            email: effectiveEmail, // Ensure email field is populated
            profile: {
              first_name: user.first_name,
              last_name: user.last_name,
              business_name: user.business_name,
              profile_type: user.profile_type
            }
          };
        });
        setUsers(validUsers);
      }
    } catch (error) {
      // Error fetching admin users - silently handle to prevent UI disruption
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalAuthUsersCount = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        setTotalAuthUsersCount(users.length); // Fallback
        return;
      }

      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'listUsers',
          page: 1,
          limit: 1, // Just get the count
          search: '',
          filter: {}
        })
      });

      if (response.ok) {
        const { total } = await response.json();
        setTotalAuthUsersCount(total);
      } else {
        // const errorData = await response.json(); // Unused
        setTotalAuthUsersCount(users.length); // Fallback
      }
    } catch (error) {
      setTotalAuthUsersCount(users.length); // Fallback
    }
  };

  const fetchPendingConfirmationsCount = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        setPendingConfirmationsCount(0); // Fallback
        return;
      }

      // Fetch a sample of users to check confirmation status
      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'listUsers',
          page: 1,
          limit: 100, // Get a sample to estimate pending confirmations
          search: '',
          filter: {}
        })
      });

      if (response.ok) {
        const { users: sampleUsers, total } = await response.json();
        
        if (sampleUsers && sampleUsers.length > 0) {
          // Count users without confirmation
          const pendingCount = sampleUsers.filter((user: any) => 
            !user.email_confirmed_at || user.email_confirmed_at === '-'
          ).length;
          
          // Estimate total pending based on sample ratio
          const estimatedPending = Math.round((pendingCount / sampleUsers.length) * total);
          setPendingConfirmationsCount(estimatedPending);
        } else {
          setPendingConfirmationsCount(0);
        }
      } else {
        setPendingConfirmationsCount(0); // Fallback
      }
    } catch (error) {
      setPendingConfirmationsCount(0); // Fallback
    }
  };

  const handleTileClick = (tileType: 'all' | 'pending' | 'admin') => {
    setSelectedTile(tileType);
    setHomeSearchTerm(''); // Clear search term when tile is selected
    setHomePage(1); // Reset to page 1 when tile changes
    
    if (tileType === 'all') {
      // Show all users - fetch from Auth system
      fetchAllUsersFromAuth();
    } else if (tileType === 'pending') {
      // Filter to show only pending confirmation users
      fetchAllUsersFromAuth();
    } else if (tileType === 'admin') {
      // Filter to show only admin users
      setFilteredUsers(users);
    }
  };

  const fetchAllUsersFromAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        return;
      }

      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'listUsers',
          page: 1,
          limit: 500, // Get all users
          search: '',
          filter: {}
        })
      });

      if (response.ok) {
        const { users: authUsers } = await response.json();
        if (authUsers && authUsers.length > 0) {
          // Fetch profile data for each user from user_profiles table
          const usersWithProfiles = await Promise.all(
            authUsers.map(async (user: any) => {
              try {
                const { data: profileData } = await supabase
                  .from('user_profiles')
                  .select('first_name, last_name, business_name, profile_type')
                  .eq('id', user.id)
                  .single();
                
                return {
                  ...user,
                  profile: profileData || {}
                };
              } catch (error) {
                return {
                  ...user,
                  profile: {}
                };
              }
            })
          );
          
          setFilteredUsers(usersWithProfiles);
        }
      }
    } catch (error) {
      // Error fetching all users - silently handle to prevent UI disruption
    }
  };

  const clearTileSelection = () => {
    setSelectedTile(null);
    setHomeSearchTerm('');
    // Clear filtered users to show default state
    setFilteredUsers([]);
  };

  // Function to fetch all users from the database
  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        return;
      }

      // Fetch all users with pagination to get complete list
      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'listUsers',
          page: 1,
          limit: 1000, // Get maximum users possible
          search: '',
          filter: {}
        })
      });

      if (response.ok) {
        const { users: allUsers, total } = await response.json();
        
        if (allUsers && allUsers.length > 0) {
          // Fetch profile data for each user to show complete information
          const usersWithProfiles = await Promise.all(
            allUsers.map(async (user: any) => {
              try {
                const { data: profileData } = await supabase
                  .from('user_profiles')
                  .select('first_name, last_name, business_name, profile_type')
                  .eq('id', user.id)
                  .single();
                
                return {
                  ...user,
                  profile: profileData || {}
                };
              } catch (error) {
                return {
                  ...user,
                  profile: {}
                };
              }
            })
          );
          
          setFilteredUsers(usersWithProfiles);
          setTotalAuthUsersCount(total);
        } else {
          setFilteredUsers([]);
        }
      } else {
        setFilteredUsers([]);
      }
    } catch (error) {
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Home page search - searches Auth system for comprehensive results
  const searchUsersInAuth = async (searchQuery: string) => {
    try {
      setLoading(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        return;
      }

              // IMPORTANT: Get ALL users first, then do comprehensive search locally
        // This ensures we can search in both metadata AND profile data
        const response = await fetch(getAdminApiUrl(), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'listUsers',
            page: 1,
            limit: 500, // Get more users for comprehensive search
            search: '', // Empty search to get ALL users
            filter: {}
          })
        });

              if (response.ok) {
          const responseData = await response.json();
          
          const { users: searchResults, total } = responseData;
          

        
        if (!searchResults || searchResults.length === 0) {
          setFilteredUsers([]);
          setTotalAuthUsersCount(total);
          return;
        }
        
        // Enhanced comprehensive search with multiple matching strategies
        const searchTerms = searchQuery.toLowerCase().trim().split(/\s+/).filter(term => term.length > 0);
        
        // IMPORTANT: Don't filter here - fetch ALL users with profiles first, then search comprehensively
        // This ensures we can search in both metadata AND profile data
        let filteredResults = searchResults;
        
        // Fetch profile data for each user from user_profiles table
        const usersWithProfiles = await Promise.all(
          filteredResults.map(async (user: any) => {
            try {
              const { data: profileData } = await supabase
                .from('user_profiles')
                .select('first_name, last_name, business_name, profile_type')
                .eq('id', user.id)
                .single();
              

              
              return {
                ...user,
                profile: profileData || {}
              };
            } catch (error) {
              
              return {
                ...user,
                profile: {}
              };
            }
          })
        );
        
        // Now search in the profile data as well for more comprehensive results
        const finalFilteredResults = usersWithProfiles.filter((user: any) => {
          // If no search terms, return all users
          if (searchTerms.length === 0) return true;
          
          // Check if ANY of the terms match in ANY field (OR logic for better results)
          const userMatches = searchTerms.some(term => {
            // 1. Email match (exact or partial)
            const emailMatch = user.email && user.email.toLowerCase().includes(term);
            
            // 2. User ID match (exact or partial)
            const idMatch = user.id && user.id.toLowerCase().includes(term);
            
            // 3. Full name from metadata (exact or partial)
            const fullNameMatch = user.user_metadata?.full_name && 
              user.user_metadata.full_name.toLowerCase().includes(term);
            
            // 4. First name from metadata (exact or partial)
            const firstNameMatch = user.user_metadata?.first_name && 
              user.user_metadata.first_name.toLowerCase().includes(term);
            
            // 5. Last name from metadata (exact or partial)
            const lastNameMatch = user.user_metadata?.last_name && 
              user.user_metadata.last_name.toLowerCase().includes(term);
            
            // 6. Business name from metadata (exact or partial)
            const businessNameMatch = user.user_metadata?.business_name && 
              user.user_metadata.business_name.toLowerCase().includes(term);
            
            // 7. Phone number match (if available)
            const phoneMatch = user.phone && user.phone.includes(term);
            
            // 8. Profile data search (THIS IS THE KEY!)
            const profileFirstNameMatch = user.profile?.first_name && 
              user.profile.first_name.toLowerCase().includes(term);
            const profileLastNameMatch = user.profile?.last_name && 
              user.profile.last_name.toLowerCase().includes(term);
            const profileBusinessNameMatch = user.profile?.business_name && 
              user.profile.business_name.toLowerCase().includes(term);
            

            
            // Return true if any of these matches the current term
            return emailMatch || idMatch || fullNameMatch || firstNameMatch || 
                   lastNameMatch || businessNameMatch || phoneMatch || profileFirstNameMatch || 
                   profileLastNameMatch || profileBusinessNameMatch;
          });
          
          return userMatches;
        });
        

        
        setFilteredUsers(finalFilteredResults);
        setTotalAuthUsersCount(total);
      } else {
        // const errorData = await response.json(); // Unused
        setFilteredUsers([]);
      }
    } catch (error) {
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // User Management page search - only searches local user_profiles table
  const searchUsersInProfiles = (searchQuery: string) => {
    if (searchQuery.trim() === '') {
      setFilteredUsers(users);
    } else {
      const searchLower = searchQuery.toLowerCase();
      const filtered = users.filter(user => {
        const email = user.email || user.account_email_id;
        return (email && email.toLowerCase().includes(searchLower)) ||
               (user.id && user.id.toLowerCase().includes(searchLower));
      });
      setFilteredUsers(filtered);
    }
  };

  const handleHomeSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (homeSearchTerm.trim()) {
      const trimmedTerm = homeSearchTerm.trim();
      
      // Check if user typed "all" to show all users
      if (trimmedTerm.toLowerCase() === 'all') {
        fetchAllUsers();
      } else {
        searchUsersInAuth(trimmedTerm);
      }
    } else {
      setFilteredUsers([]);
    }
  };

  const handleUserManagementSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (userManagementSearchTerm.trim()) {
      const trimmedTerm = userManagementSearchTerm.trim();
      searchUsersInProfiles(trimmedTerm);
    } else {
      setFilteredUsers(users);
    }
  };

  // Action button handlers
  const handleViewProfile = (userId: string) => {
    // Save current dashboard state and navigate to user profile page
    const dashboardState = {
      activeSection,
      homeSearchTerm,
      filteredUsers,
      userManagementSearchTerm,
      currentPage
    };
    localStorage.setItem('dashboardState', JSON.stringify(dashboardState));
    navigate(`/user-profile/${userId}`, { state: { dashboardState } });
  };

  const handleViewProducts = (userId: string) => {
    // Save current dashboard state and navigate to user products page
    const dashboardState = {
      activeSection,
      homeSearchTerm,
      filteredUsers,
      userManagementSearchTerm,
      currentPage
    };
    localStorage.setItem('dashboardState', JSON.stringify(dashboardState));
    navigate(`/user-products/${userId}`, { state: { dashboardState } });
  };

  const handleViewJobs = (userId: string) => {
    // Save current dashboard state and navigate to user jobs page
    const dashboardState = {
      activeSection,
      homeSearchTerm,
      filteredUsers,
      userManagementSearchTerm,
      currentPage
    };
    localStorage.setItem('dashboardState', JSON.stringify(dashboardState));
    navigate(`/user-jobs-services/${userId}`, { state: { dashboardState } });
  };

  const handleViewRentals = (userId: string) => {
    // Save current dashboard state and navigate to user rentals page
    const dashboardState = {
      activeSection,
      homeSearchTerm,
      filteredUsers,
      userManagementSearchTerm,
      currentPage
    };
    localStorage.setItem('dashboardState', JSON.stringify(dashboardState));
    navigate(`/user-rentals/${userId}`, { state: { dashboardState } });
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      // Error signing out
    }
  };

  // Create Admin User Functions
  const handleCreateAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (createAdminForm.password !== createAdminForm.confirmPassword) {
      setCreateAdminError('Passwords do not match');
      return;
    }
    
    // Validate password strength
    const passwordValidation = validatePassword(createAdminForm.password);
    if (!passwordValidation.isValid) {
      setCreateAdminError(passwordValidation.errors.join(', '));
      return;
    }
    
    if (!createAdminForm.email || !createAdminForm.password) {
      setCreateAdminError('Email and password are required');
      return;
    }

    try {
      setCreateAdminLoading(true);
      setCreateAdminError(null);
      setCreateAdminSuccess(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No active session');
      }

      // Step 1: Create user in Auth system using Admin API
      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'createUser',
          data: {
            email: createAdminForm.email,
            password: createAdminForm.password,
            email_confirm: true,
            metadata: {
              full_name: createAdminForm.profileType === 'business' 
                ? createAdminForm.businessName 
                : `${createAdminForm.firstName} ${createAdminForm.lastName}`.trim()
            }
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create user in Auth system');
      }

      const { user: authUser } = await response.json();

      // Step 2: Create profile in user_profiles table using direct Supabase client
      const now = new Date().toISOString();
      const { error: profileError } = await supabase.from('user_profiles').insert({
        id: authUser.id,
        first_name: createAdminForm.firstName || null,
        last_name: createAdminForm.lastName || null,
        profile_type: createAdminForm.profileType,
        business_name: createAdminForm.businessName || null,
        abn_number: createAdminForm.abnNumber || null,
        created_at: now,
        updated_at: now,
        country_code: 'AU',
        phone: '',
        about_text: null,
        individual_image_url: null,
        business_image_url: null,
        individual_interests: '{}',
        individual_skills: '{}',
        business_expertise: '{}',
        default_profile: createAdminForm.profileType,
        email: createAdminForm.email,
        stripe_account_id: null,
        stripe_customer_id: null,
        expo_push_token: null,
        country_name: 'Australia',
        currency_code: 'AUD',
        currency_symbol: '$',
        currency_name: 'Australian Dollar',
        is_admin: true,
        email_verified: true,
        email_verified_at: now,
        phone_verified: false,
        phone_verified_at: null,
        business_stripe_account_id: null,
        business_stripe_customer_id: null,
        business_rep_first_name: createAdminForm.firstName || null,
        business_rep_last_name: createAdminForm.lastName || null,
        city_name: 'Melbourne',
        state_name: 'Victoria',
        city_latitude: '-37.81362760',
        city_longitude: '144.96305760',
        state_latitude: null,
        state_longitude: null,
        phone_country_code: '+61',
        admin_role: createAdminForm.adminRole
      });

      if (profileError) {
        // If profile creation fails, we should clean up the auth user
        // But for now, just throw the error
        throw new Error(`Failed to create user profile: ${profileError.message}`);
      }

      setCreateAdminSuccess(`Admin user ${createAdminForm.email} created successfully!`);
      setCreateAdminForm({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        businessName: '',
        abnNumber: '',
        profileType: 'individual',
        adminRole: 'admin'
      });
      // Refresh the users list
      fetchUsers();
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowCreateAdminModal(false);
        setCreateAdminSuccess(null);
      }, 2000);
    } catch (error: any) {
      setCreateAdminError(error.message || 'Failed to create admin user');
    } finally {
      setCreateAdminLoading(false);
    }
  };

  const handleCreateAdminFormChange = (field: string, value: string) => {
    setCreateAdminForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetCreateAdminForm = () => {
    setCreateAdminForm({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      businessName: '',
      abnNumber: '',
      profileType: 'individual',
      adminRole: 'admin'
    });
    setCreateAdminError(null);
    setCreateAdminSuccess(null);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <div className={`${sidebarExpanded ? 'w-64' : 'w-20'} bg-white shadow-lg flex flex-col transition-all duration-300 ease-in-out`}>
        <div className="p-6 flex-1">
          <div className="flex items-center mb-4">
            <div className="mr-3 flex-shrink-0">
              <Logo size={40} />
            </div>
            {sidebarExpanded && (
            <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
            )}
          </div>
          
          {/* Sidebar Toggle Button */}
          <div className="mb-6">
            <button
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              className={`flex items-center justify-center p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200 text-gray-600 ${
                sidebarExpanded ? 'w-full' : 'w-10 h-10 mx-auto'
              }`}
              title={sidebarExpanded ? 'Collapse Sidebar' : 'Expand Sidebar'}
            >
              <svg 
                className="w-5 h-5 flex-shrink-0" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                {sidebarExpanded ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                )}
              </svg>
              {sidebarExpanded && (
                <span className="ml-2 text-sm font-medium">Collapse</span>
              )}
            </button>
          </div>
          
          {/* Navigation Menu */}
          <nav className="space-y-2">
            <button
              onClick={() => {
                setActiveSection('home');
                // Only clear search and results if we're not restoring state
                if (!location.state?.preserveState) {
                  setHomeSearchTerm(''); // Clear home search when switching to home
                  setFilteredUsers([]); // Clear filtered results
                }
              }}
              className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                sidebarExpanded ? 'w-full' : 'w-10 h-10 mx-auto justify-center'
              } ${
                activeSection === 'home'
                  ? 'bg-indigo-50 text-indigo-700 border-r-2 border-indigo-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
              title={sidebarExpanded ? 'Home' : 'Home'}
            >
              <svg className={`w-6 h-6 flex-shrink-0 ${sidebarExpanded ? 'mr-3' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              {sidebarExpanded && <span>Home</span>}
            </button>
            
            {adminRole === 'super_admin' ? (
              <button
                onClick={() => {
                  setActiveSection('users');
                  // Only clear search and results if we're not restoring state
                  if (!location.state?.preserveState) {
                    setUserManagementSearchTerm(''); // Clear user management search when switching
                    setFilteredUsers(users); // Show all users
                  }
                }}
                className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  sidebarExpanded ? 'w-full' : 'w-10 h-10 mx-auto justify-center'
                } ${
                  activeSection === 'users'
                    ? 'bg-indigo-50 text-indigo-700 border-r-2 border-indigo-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                title={sidebarExpanded ? 'User Management' : 'User Management'}
              >
                <svg className={`w-6 h-6 flex-shrink-0 ${sidebarExpanded ? 'mr-3' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                {sidebarExpanded && <span>User Management</span>}
              </button>
            ) : (
              <div
                className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  sidebarExpanded ? 'w-full' : 'w-10 h-10 mx-auto justify-center'
                } text-gray-400 cursor-not-allowed opacity-50`}
                title="User Management - Super Admin Only"
              >
                <svg className={`w-6 h-6 flex-shrink-0 ${sidebarExpanded ? 'mr-3' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 0 2.5 2.5 0 015 0z" />
                </svg>
                {sidebarExpanded && (
                  <div className="flex items-center">
                    <span>User Management</span>
                    <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Super Admin Only</span>
                  </div>
                )}
              </div>
            )}
            
            <button
              onClick={() => navigate('/service-fees')}
              className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                sidebarExpanded ? 'w-full' : 'w-10 h-10 mx-auto justify-center'
              } ${
                window.location.pathname === '/service-fees'
                  ? 'bg-indigo-50 text-indigo-700 border-r-2 border-indigo-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
              title={sidebarExpanded ? 'Service Fees' : 'Service Fees'}
            >
              <svg className={`w-6 h-6 flex-shrink-0 ${sidebarExpanded ? 'mr-3' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
              {sidebarExpanded && <span>Service Fees</span>}
            </button>
            
            <button
              onClick={() => navigate('/customer-feedback')}
              className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                sidebarExpanded ? 'w-full' : 'w-10 h-10 mx-auto justify-center'
              } ${
                window.location.pathname === '/customer-feedback'
                  ? 'bg-indigo-50 text-indigo-700 border-r-2 border-indigo-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
              title={sidebarExpanded ? 'Customer Feedback' : 'Customer Feedback'}
            >
              <svg className={`w-6 h-6 flex-shrink-0 ${sidebarExpanded ? 'mr-3' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {sidebarExpanded && <span>Customer Feedback</span>}
            </button>

            {/* Inappropriate Content Flags */}
            <button
              onClick={() => navigate('/inappropriate-flags')}
              className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                sidebarExpanded ? 'w-full' : 'w-10 h-10 mx-auto justify-center'
              } ${
                window.location.pathname === '/inappropriate-flags'
                  ? 'bg-indigo-50 text-indigo-700 border-r-2 border-indigo-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
              title={sidebarExpanded ? 'Inappropriate Content Flags' : 'Inappropriate Content Flags'}
            >
              <svg className={`w-6 h-6 flex-shrink-0 ${sidebarExpanded ? 'mr-3' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {sidebarExpanded && <span>Inappropriate Flags</span>}
            </button>

            {/* Account Deletion Requests */}
            <button
              onClick={() => navigate('/account-deletion-requests')}
              className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                sidebarExpanded ? 'w-full' : 'w-10 h-10 mx-auto justify-center'
              } ${
                window.location.pathname === '/account-deletion-requests'
                  ? 'bg-indigo-50 text-indigo-700 border-r-2 border-indigo-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
              title={sidebarExpanded ? 'Account Deletion Requests' : 'Account Deletion Requests'}
            >
              <svg className={`w-6 h-6 flex-shrink-0 ${sidebarExpanded ? 'mr-3' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {sidebarExpanded && <span>Account Deletions</span>}
            </button>
          </nav>
        </div>
        
        {/* User Info & Sign Out */}
        <div className="p-6 border-t border-gray-200 bg-white">
          <div className={`flex items-center mb-4 ${sidebarExpanded ? '' : 'justify-center'}`}>
            <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
              <span className="text-indigo-600 text-sm font-medium">
                {user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
              </span>
            </div>
            {sidebarExpanded && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.email || 'User'}
              </p>
              <div className="flex items-center space-x-2 mt-1">
                {adminRole && (
                  <span className={`text-xs font-medium ${
                    adminRole === 'super_admin' 
                      ? 'text-purple-600 bg-purple-100 px-2 py-1 rounded' 
                      : 'text-blue-600 bg-blue-100 px-2 py-1 rounded'
                  }`}>
                    {adminRole === 'super_admin' ? 'Super Admin' : adminRole}
                  </span>
                )}
                {/* MFA Status Badge */}
                <span className={`text-xs font-medium px-2 py-1 rounded ${
                  hasMFA 
                    ? 'text-green-600 bg-green-100' 
                    : 'text-amber-600 bg-amber-100'
                }`}>
                  {hasMFA ? 'MFA ✓' : 'MFA ✗'}
                </span>
              </div>
            </div>
            )}
          </div>
          <div className={`${sidebarExpanded ? 'space-y-2' : 'flex justify-center space-x-2'}`}>
            {/* MFA Settings Button */}
            <button
              onClick={() => setShowMFAModal(true)}
              className={`flex items-center justify-center px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors duration-200 ${
                sidebarExpanded ? 'w-full' : 'w-10 h-10'
              }`}
              title={sidebarExpanded ? 'MFA Settings' : 'MFA Settings'}
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              {sidebarExpanded && <span className="ml-2">MFA Settings</span>}
            </button>
            
            {/* Sign Out Button */}
            <button
              onClick={handleSignOut}
              className={`flex items-center justify-center px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors duration-200 ${
                sidebarExpanded ? 'w-full' : 'w-10 h-10'
              }`}
              title={sidebarExpanded ? 'Sign Out' : 'Sign Out'}
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {sidebarExpanded && <span className="ml-2">Sign Out</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Header */}
        <header className="bg-[#00a67e] shadow-sm border-b border-[#008f6b]">
          <div className="px-6 py-4">
            <h2 className="text-2xl font-bold text-white">
              {activeSection === 'home' ? 'Dashboard Home' : 'User Management'}
            </h2>
            <p className="text-green-100 mt-1">
              {activeSection === 'home' 
                ? 'Search and manage users by ID' 
                : adminRole === 'super_admin' 
                  ? 'View and manage all registered users'
                  : 'Access Restricted - Super Admin Only'
              }
            </p>
          </div>
        </header>

              {/* Main Content */}
        <main className="flex-1 p-6">
          {activeSection === 'home' ? (
            /* Home Section */
            <div>
                {/* Search Bar */}
              <div className="mb-8">
                <form onSubmit={handleHomeSearch} className="max-w-lg">
                  <label htmlFor="homeSearch" className="sr-only">Search users</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      id="homeSearch"
                      name="homeSearch"
                      className="block w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base transition-all duration-200"
                      placeholder="Search by email, ID, first name, last name, business name, or phone number... (type 'all' to show all users)"
                      type="search"
                      value={homeSearchTerm}
                      onChange={(e) => setHomeSearchTerm(e.target.value)}
                    />
                  </div>
                </form>
                
                {/* Search Tips Tooltip */}
                <div className="mt-3 flex items-center">
                  <div className="relative group">
                    <div className="cursor-help">
                      <svg className="w-5 h-5 text-amber-500 hover:text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    
                    {/* Tooltip Content */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                      <div className="font-medium mb-1">Search Tips:</div>
                      <div className="space-y-1">
                        <div>• Full name (e.g., "John Smith")</div>
                        <div>• Email address (partial or full)</div>
                        <div>• User ID (partial or full)</div>
                        <div>• Business name (partial or full)</div>
                        <div>• Phone number (if available)</div>
                      </div>
                      <div className="mt-2 pt-1 border-t border-gray-700 text-gray-300">
                        Multiple words find users matching ANY term
                      </div>
                      
                      {/* Tooltip Arrow */}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                  <span className="ml-2 text-xs text-gray-500">Hover for search tips</span>
                </div>
                
                {/* Active Filter Display */}
                {selectedTile && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-amber-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                          </svg>
                          <span className="font-medium text-amber-900">Active Filter:</span>
                          <span className="ml-2 text-amber-800 font-mono bg-amber-100 px-2 py-1 rounded">
                            {selectedTile === 'all' ? 'All Users' : 
                             selectedTile === 'pending' ? 'Pending Confirmation' : 
                             'Admin Users Only'}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={clearTileSelection}
                        className="flex items-center px-3 py-2 text-sm font-medium text-amber-700 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors duration-200"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Clear Filter
                      </button>
                    </div>
                  </div>
                )}
                </div>
                
                {/* Quick Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div 
                    className={`bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
                      selectedTile === 'all' ? 'ring-2 ring-blue-400 shadow-lg' : ''
                    }`}
                    onClick={() => handleTileClick('all')}
                    title="Click to show all users"
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 bg-blue-500 rounded-lg flex items-center justify-center">
                          <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-blue-600">Total Users</p>
                        <p className="text-3xl font-bold text-blue-900">{totalAuthUsersCount}</p>
                        <p className="text-blue-600 text-sm">From Auth System</p>
                      </div>
                    </div>
                  </div>
                
                <div 
                  className={`bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border border-green-100 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
                    selectedTile === 'pending' ? 'ring-2 ring-green-400 shadow-lg' : ''
                  }`}
                  onClick={() => handleTileClick('pending')}
                  title="Click to show users pending confirmation"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 bg-green-500 rounded-lg flex items-center justify-center">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-green-600">Users Pending Confirmation</p>
                      <p className="text-green-600 text-sm">
                        {homeSearchTerm && filteredUsers.length > 0 
                          ? 'From search results'
                          : 'Estimated from sample'
                        }
                      </p>
                      <p className="text-2xl font-bold text-green-900">
                        {homeSearchTerm && filteredUsers.length > 0 
                          ? filteredUsers.filter(u => !(u as any).email_confirmed_at || (u as any).email_confirmed_at === '-').length
                          : pendingConfirmationsCount
                        }
                      </p>
                    </div>
                  </div>
                </div>
                
                <div 
                  className={`bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-6 border border-purple-100 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
                    selectedTile === 'admin' ? 'ring-2 ring-purple-400 shadow-lg' : ''
                  }`}
                  onClick={() => handleTileClick('admin')}
                  title="Click to show admin users only"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 bg-purple-500 rounded-lg flex items-center justify-center">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-purple-600">Admin Users</p>
                      <p className="text-2xl font-bold text-purple-900">{users.filter(u => u.is_admin).length}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search Results or Filtered Results */}
              {(homeSearchTerm || selectedTile) && (
                <div className="mt-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {homeSearchTerm ? 'Search Results' : 'Filtered Results'}
                  </h3>
                  
                  {/* Search Summary */}
                  <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <span className="font-medium text-blue-900">
                            {homeSearchTerm ? 'Search Query:' : 'Active Filter:'}
                          </span>
                          <span className="ml-2 text-blue-800 font-mono bg-blue-100 px-2 py-1 rounded">
                            {homeSearchTerm ? `"${homeSearchTerm}"` : 
                             selectedTile === 'all' ? 'All Users' : 
                             selectedTile === 'pending' ? 'Pending Confirmation' : 
                             'Admin Users Only'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium text-green-900">Results Found:</span>
                        <span className="ml-2 text-green-800 font-bold text-lg">{filteredUsers.length}</span>
                      </div>
                    </div>
                  </div>
                 
                  {(() => {
                    // Pagination calculations for home page
                    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
                    const startIndex = (homePage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
                    
                    return (
                      <>
                        {loading ? (
                          <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                          </div>
                        ) : (
                          <div className="bg-white shadow overflow-hidden sm:rounded-md">
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      User
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Status & Profile Type
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Role
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Joined
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Last Sign In
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Actions
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {paginatedUsers.map((user) => (
                              <tr key={user.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10">
                                      <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                        <span className="text-sm font-medium text-indigo-600">
                                          {user.email ? user.email.charAt(0).toUpperCase() : '?'}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="ml-4">
                                      {/* Display name based on profile type */}
                                      {user.profile?.profile_type === 'business' && user.profile?.business_name ? (
                                        <div className="text-sm font-medium text-gray-900">{user.profile.business_name}</div>
                                      ) : user.profile?.first_name || user.profile?.last_name ? (
                                        <div className="text-sm font-medium text-gray-900">
                                          {[user.profile.first_name, user.profile.last_name].filter(Boolean).join(' ')}
                                        </div>
                                      ) : (
                                        <div className="text-sm font-medium text-gray-900">{user.email || 'No email'}</div>
                                      )}
                                      <div className="text-sm text-gray-500">{user.email || 'No email'}</div>
                                      <div className="text-sm text-gray-500">ID: {user.id}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="space-y-2">
                                    {/* Profile Type Badge */}
                                    <div>
                                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                        user.profile?.profile_type === 'business' 
                                          ? 'bg-purple-100 text-purple-800'
                                          : user.profile?.profile_type === 'individual'
                                          ? 'bg-blue-100 text-blue-800'
                                          : 'bg-gray-100 text-gray-800'
                                      }`}>
                                        {user.profile?.profile_type === 'business' 
                                          ? 'Business' 
                                          : user.profile?.profile_type === 'individual'
                                          ? 'Individual'
                                          : 'Unknown'
                                        }
                                      </span>
                                    </div>
                                    {/* Status Badge */}
                                    <div>
                                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                        (user as any).email_confirmed_at && (user as any).email_confirmed_at !== '-'
                                          ? 'bg-green-100 text-green-800' 
                                          : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {(user as any).email_confirmed_at && (user as any).email_confirmed_at !== '-' ? 'Confirmed' : 'Pending'}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    user.is_admin 
                                      ? 'bg-purple-100 text-purple-800' 
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {user.is_admin ? 'Admin' : 'User'}
                                  </span>
                                  {user.admin_role && (
                                    <div className="text-xs text-gray-500 mt-1">{user.admin_role}</div>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {new Date(user.created_at).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(user as any).last_sign_in_at || user.last_sign_in 
                                    ? new Date((user as any).last_sign_in_at || user.last_sign_in).toLocaleDateString()
                                    : 'Never'
                                  }
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {!user.is_admin ? (
                                    <div className="flex flex-wrap gap-2">
                                      <button
                                        onClick={() => handleViewProfile(user.id)}
                                        className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                                        title="View Profile"
                                      >
                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        Profile
                                      </button>
                                      <button
                                        onClick={() => handleViewProducts(user.id)}
                                        className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
                                        title="View Products"
                                      >
                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                        </svg>
                                        Products
                                      </button>
                                      <button
                                        onClick={() => handleViewJobs(user.id)}
                                        className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                        title="View Jobs & Services"
                                      >
                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        Jobs
                                      </button>
                                      <button
                                        onClick={() => handleViewRentals(user.id)}
                                        className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-200"
                                        title="View Rentals"
                                      >
                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                        Rentals
                                      </button>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs italic">Admin user - no actions available</span>
                                  )}
                                </td>
                              </tr>
                                  ))}
                                </tbody>
                              </table>
                              
                              {filteredUsers.length === 0 && (
                                <div className="px-4 py-8 text-center text-gray-500">
                                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                  </svg>
                                  <div className="text-lg font-medium text-gray-600 mb-2">No users found</div>
                                  <div className="text-sm text-gray-400">Try adjusting your search terms or search by email address</div>
                                </div>
                              )}
                              
                              {/* Pagination Controls */}
                              {filteredUsers.length > 0 && totalPages > 1 && (
                                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                                  <div className="flex-1 flex justify-between sm:hidden">
                                    <button
                                      onClick={() => setHomePage(Math.max(1, homePage - 1))}
                                      disabled={homePage === 1}
                                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                        homePage === 1
                                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                          : 'bg-white text-gray-700 hover:bg-gray-50'
                                      }`}
                                    >
                                      Previous
                                    </button>
                                    <button
                                      onClick={() => setHomePage(Math.min(totalPages, homePage + 1))}
                                      disabled={homePage === totalPages}
                                      className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                        homePage === totalPages
                                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                          : 'bg-white text-gray-700 hover:bg-gray-50'
                                      }`}
                                    >
                                      Next
                                    </button>
                                  </div>
                                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                    <div>
                                      <p className="text-sm text-gray-700">
                                        Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                                        <span className="font-medium">{Math.min(endIndex, filteredUsers.length)}</span> of{' '}
                                        <span className="font-medium">{filteredUsers.length}</span> results
                                      </p>
                                    </div>
                                    <div>
                                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                        <button
                                          onClick={() => setHomePage(Math.max(1, homePage - 1))}
                                          disabled={homePage === 1}
                                          className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                                            homePage === 1
                                              ? 'text-gray-300 cursor-not-allowed'
                                              : 'text-gray-500 hover:bg-gray-50'
                                          }`}
                                        >
                                          <span className="sr-only">Previous</span>
                                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                        </button>
                                        {[...Array(totalPages)].map((_, i) => {
                                          const pageNum = i + 1;
                                          // Show first page, last page, current page, and pages around current
                                          if (
                                            pageNum === 1 ||
                                            pageNum === totalPages ||
                                            (pageNum >= homePage - 1 && pageNum <= homePage + 1)
                                          ) {
                                            return (
                                              <button
                                                key={pageNum}
                                                onClick={() => setHomePage(pageNum)}
                                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                  homePage === pageNum
                                                    ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                }`}
                                              >
                                                {pageNum}
                                              </button>
                                            );
                                          } else if (
                                            pageNum === homePage - 2 ||
                                            pageNum === homePage + 2
                                          ) {
                                            return (
                                              <span
                                                key={pageNum}
                                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                                              >
                                                ...
                                              </span>
                                            );
                                          }
                                          return null;
                                        })}
                                        <button
                                          onClick={() => setHomePage(Math.min(totalPages, homePage + 1))}
                                          disabled={homePage === totalPages}
                                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                                            homePage === totalPages
                                              ? 'text-gray-300 cursor-not-allowed'
                                              : 'text-gray-500 hover:bg-gray-50'
                                          }`}
                                        >
                                          <span className="sr-only">Next</span>
                                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                          </svg>
                                        </button>
                                      </nav>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          ) : adminRole === 'super_admin' ? (
            /* User Management Section - Only visible to super_admin */
            <div>
              {/* Search Bar and Create Admin Button */}
              <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <form onSubmit={handleUserManagementSearch} className="max-w-lg flex-1">
                  <label htmlFor="userManagementSearch" className="sr-only">Search users</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      id="userManagementSearch"
                      name="userManagementSearch"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Search admin users by email or ID (searches user profiles)..."
                      type="search"
                      value={userManagementSearchTerm}
                      onChange={(e) => setUserManagementSearchTerm(e.target.value)}
                    />
                  </div>
                </form>
                
                <button
                  onClick={() => {
                    resetCreateAdminForm();
                    setShowCreateAdminModal(true);
                  }}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-sm"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Admin User
                </button>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 bg-blue-500 rounded-lg flex items-center justify-center">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-blue-600">Total Admin Users</p>
                      <p className="text-2xl font-bold text-blue-900">{users.length}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border border-green-100">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 bg-green-500 rounded-lg flex items-center justify-center">
                          <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-green-600">Super Admins</p>
                        <p className="text-2xl font-bold text-green-900">{users.filter(u => u.admin_role === 'super_admin').length}</p>
                      </div>
                    </div>
                  </div>
                  
                                    <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-6 border border-purple-100">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 bg-purple-500 rounded-lg flex items-center justify-center">
                          <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-purple-600">Other Admin Roles</p>
                        <p className="text-2xl font-bold text-purple-900">
                          {users.filter(u => u.admin_role && u.admin_role !== 'super_admin').length}
                        </p>
                        <div className="text-xs text-purple-600 mt-1">
                          Admin: {users.filter(u => u.admin_role === 'admin').length} | 
                          Support: {users.filter(u => u.admin_role === 'support').length} | 
                          Marketing: {users.filter(u => u.admin_role === 'marketing').length} | 
                          Viewer: {users.filter(u => u.admin_role === 'viewer').length}
                        </div>
                      </div>
                    </div>
                  </div>
              </div>

          {/* Users Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Admin User Management
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                View and manage admin users only
              </p>
            </div>
            
            {loading ? (
              <div className="px-4 py-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Profile Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Joined
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.filter(user => user && user.id).map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-indigo-600">
                                  {user.email ? user.email.charAt(0).toUpperCase() : '?'}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              {/* Display name based on profile type */}
                              {user.profile?.profile_type === 'business' && user.profile?.business_name ? (
                                <div className="text-sm font-medium text-gray-900">{user.profile.business_name}</div>
                              ) : user.profile?.first_name || user.profile?.last_name ? (
                                <div className="text-sm font-medium text-gray-900">
                                  {[user.profile.first_name, user.profile.last_name].filter(Boolean).join(' ')}
                                </div>
                              ) : (
                                <div className="text-sm font-medium text-gray-900">{user.email || 'No email'}</div>
                              )}
                              <div className="text-sm text-gray-500">{user.email || 'No email'}</div>
                              <div className="text-sm text-gray-500">ID: {user.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.profile?.profile_type === 'business' 
                              ? 'bg-purple-100 text-purple-800'
                              : user.profile?.profile_type === 'individual'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.profile?.profile_type === 'business' 
                              ? 'Business' 
                              : user.profile?.profile_type === 'individual'
                              ? 'Individual'
                              : 'Unknown'
                            }
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.is_admin 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.is_admin ? 'Admin' : 'User'}
                          </span>
                          {user.admin_role && (
                            <div className="text-xs text-gray-500 mt-1">{user.admin_role}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button 
                            onClick={() => navigate(`/user-profile-view/${user.id}`)}
                            className="text-indigo-600 hover:text-indigo-900 mr-3"
                          >
                            View
                          </button>
                          <button className="text-red-600 hover:text-red-900">
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                                        {filteredUsers.length === 0 && (
                          <div className="px-4 py-8 text-center text-gray-500">
                            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                            </svg>
                            <div className="text-lg font-medium text-gray-600 mb-2">
                              {userManagementSearchTerm ? 'No admin users found matching your search.' : 'No admin users found.'}
                            </div>
                            <div className="text-sm text-gray-400">Try adjusting your search terms</div>
                          </div>
                        )}
              </div>
            )}
          </div>

            </div>
          ) : (
            /* Access Denied for non-super_admin users */
            <div className="text-center py-12">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Access Restricted</h3>
              <p className="text-gray-600 mb-4">
                User Management section is only accessible to Super Administrators.
              </p>
              <p className="text-sm text-gray-500">
                Your current role: <span className="font-medium">{adminRole || 'No role assigned'}</span>
              </p>
            </div>
          )}
        </main>
      </div>
      
      {/* MFA Management Modal */}
      {showMFAModal && (
        <MFAManagement onClose={() => setShowMFAModal(false)} />
      )}

      {/* Create Admin User Modal */}
      {showCreateAdminModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create New Admin User</h3>
                <button
                  onClick={() => {
                    setShowCreateAdminModal(false);
                    resetCreateAdminForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {createAdminError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{createAdminError}</p>
                    </div>
                  </div>
                </div>
              )}

              {createAdminSuccess && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-800">{createAdminSuccess}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleCreateAdminSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      value={createAdminForm.email}
                      onChange={(e) => handleCreateAdminFormChange('email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="admin@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="adminRole" className="block text-sm font-medium text-gray-700 mb-1">
                      Admin Role *
                    </label>
                    <select
                      id="adminRole"
                      required
                      value={createAdminForm.adminRole}
                      onChange={(e) => handleCreateAdminFormChange('adminRole', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="admin">Admin</option>
                      <option value="support">Support</option>
                      <option value="marketing">Marketing</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      id="password"
                      required
                      value={createAdminForm.password}
                      onChange={(e) => handleCreateAdminFormChange('password', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter strong password"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Must be 8+ characters with uppercase, lowercase, number, and special character
                    </p>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      required
                      value={createAdminForm.confirmPassword}
                      onChange={(e) => handleCreateAdminFormChange('confirmPassword', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Confirm password"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="profileType" className="block text-sm font-medium text-gray-700 mb-1">
                    Profile Type
                  </label>
                  <select
                    id="profileType"
                    value={createAdminForm.profileType}
                    onChange={(e) => handleCreateAdminFormChange('profileType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="individual">Individual</option>
                    <option value="business">Business</option>
                  </select>
                </div>

                {createAdminForm.profileType === 'individual' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        value={createAdminForm.firstName}
                        onChange={(e) => handleCreateAdminFormChange('firstName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="John"
                      />
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        value={createAdminForm.lastName}
                        onChange={(e) => handleCreateAdminFormChange('lastName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">
                        Business Name
                      </label>
                      <input
                        type="text"
                        id="businessName"
                        value={createAdminForm.businessName}
                        onChange={(e) => handleCreateAdminFormChange('businessName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Company Name"
                      />
                    </div>
                    <div>
                      <label htmlFor="abnNumber" className="block text-sm font-medium text-gray-700 mb-1">
                        ABN Number
                      </label>
                      <input
                        type="text"
                        id="abnNumber"
                        value={createAdminForm.abnNumber}
                        onChange={(e) => handleCreateAdminFormChange('abnNumber', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="12345678901"
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateAdminModal(false);
                      resetCreateAdminForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createAdminLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createAdminLoading ? (
                      <div className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating...
                      </div>
                    ) : (
                      'Create Admin User'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
